import { useCallback, useMemo } from 'react';
import type { ApolloCache, RefetchQueriesInclude } from '@apollo/client';
import {
  useActivateProjectMutation,
  useDeactivateProjectMutation,
  WorkspaceInfoBasic,
} from '@frameio/graphql-client/generated';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { ToastAlert, ToastButton, useToast } from '@frameio/vapor';
import { GetAllProjects } from '../../projects/gql/generated/GetAllProjects';
import { GetProjectsInWorkspace } from '../gql/generated/GetProjectsInWorkspace';
import TEXT from './generated/TEXT/useProjectStatusToggle';

const TOAST_DURATION = 2000;

TEXT.DEFINE({
  /** Button label to undo status change */
  undo: 'Undo',
  /** Success toast message after toggling project status */
  success: (name: string, isDeactivated: boolean) =>
    `"${name}" project is marked ${isDeactivated ? 'Inactive' : 'Active'}`,
  /** Success toast message after undoing the status change */
  successUndo: (isUndoDeactivate: boolean) =>
    `Marking ${isUndoDeactivate ? 'inactive' : 'active'} undone`,
  /** Error toast title when status update fails */
  failure: 'Something went wrong',
  /** Button label to retry a failed status update */
  retry: 'Retry',
});

type UseProjectStatusToggleProps = {
  id: string;
  name: string;
  workspaceId: string;
  archivedAt?: string | null;
  trackingSource: TrackingSourceType;
  refetch?: boolean;
};

type UpdateProjectCountsOption = {
  isDeactivated: boolean;
};

const getUpdatedProjectCounts = (
  visibleActiveProjectCount: number | null = 0,
  visibleInactiveProjectCount: number | null = 0,
  opt: UpdateProjectCountsOption = { isDeactivated: false }
) => {
  if (opt.isDeactivated) {
    return {
      visibleActiveProjectCount: visibleActiveProjectCount
        ? visibleActiveProjectCount - 1
        : 0,
      visibleInactiveProjectCount: visibleInactiveProjectCount
        ? visibleInactiveProjectCount + 1
        : 1,
    };
  }
  return {
    visibleActiveProjectCount: visibleActiveProjectCount
      ? visibleActiveProjectCount + 1
      : 1,
    visibleInactiveProjectCount: visibleInactiveProjectCount
      ? visibleInactiveProjectCount - 1
      : 0,
  };
};

const updateWorkspaceProjectCounts = (
  cache: ApolloCache<any>,
  workspaceId?: string | null,
  opt?: UpdateProjectCountsOption
) => {
  if (workspaceId) {
    cache.updateFragment(
      {
        fragment: WorkspaceInfoBasic,
        fragmentName: 'WorkspaceInfoBasic',
        id: cache.identify({
          __typename: 'Workspace',
          id: workspaceId,
        }),
      },
      (value) => {
        if (!value) return value;
        const updatedProjectCounts = getUpdatedProjectCounts(
          value.visibleActiveProjectCount,
          value.visibleInactiveProjectCount,
          opt
        );
        return {
          ...value,
          visibleActiveProjectCount:
            updatedProjectCounts.visibleActiveProjectCount,
          visibleInactiveProjectCount:
            updatedProjectCounts.visibleInactiveProjectCount,
        };
      }
    );
  }
};

export const useProjectStatusToggle = ({
  id,
  name,
  workspaceId,
  archivedAt,
  trackingSource,
  refetch = false,
}: UseProjectStatusToggleProps) => {
  const { addToast } = useToast();
  const [activateProjectMutation] = useActivateProjectMutation();
  const [deactivateProjectMutation] = useDeactivateProjectMutation();
  const track = useTrackUnsafe();
  const refetchQueries: RefetchQueriesInclude | undefined = useMemo(
    () => (refetch ? [GetAllProjects, GetProjectsInWorkspace] : undefined),
    [refetch]
  );
  const activateProject = useCallback(
    ({
      onCompleted,
      onError,
    }: {
      onCompleted: () => void;
      onError: () => void;
    }) => {
      activateProjectMutation({
        variables: {
          input: {
            id,
          },
        },
        update(cache, result) {
          updateWorkspaceProjectCounts(
            cache,
            result?.data?.activateProject?.project?.workspaceId
          );
        },
        onCompleted,
        onError,
        optimisticResponse: {
          __typename: 'Mutation',
          activateProject: {
            __typename: 'ActivateProjectPayload',
            project: {
              id,
              workspaceId,
              archivedAt: null,
            },
          },
        },
        refetchQueries,
      });
    },
    [activateProjectMutation, id, workspaceId, refetchQueries]
  );
  const deactivateProject = useCallback(
    ({
      onCompleted,
      onError,
    }: {
      onCompleted: () => void;
      onError: () => void;
    }) => {
      deactivateProjectMutation({
        variables: {
          input: {
            id,
          },
        },
        update(cache, result) {
          updateWorkspaceProjectCounts(
            cache,
            result?.data?.deactivateProject?.project?.workspaceId,
            { isDeactivated: true }
          );
        },
        onCompleted,
        onError,
        optimisticResponse: {
          __typename: 'Mutation',
          deactivateProject: {
            __typename: 'DeactivateProjectPayload',
            project: {
              id,
              workspaceId,
              archivedAt: new Date().toISOString(),
            },
          },
        },
        refetchQueries,
      });
    },
    [deactivateProjectMutation, id, workspaceId, refetchQueries]
  );

  const { undo, success, successUndo, failure, retry } = TEXT;

  const addSuccessToast = useCallback(
    ({
      isDeactivated,
      undoAction,
      isUndo = false,
    }: {
      isDeactivated: boolean;
      undoAction?: () => void;
      isUndo?: boolean;
    }) => {
      addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            adornmentAfter={
              isUndo ? null : (
                <ToastButton
                  onPress={() => {
                    undoAction?.();
                    closeToast();
                  }}
                >
                  {undo}
                </ToastButton>
              )
            }
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={
              isUndo
                ? (successUndo(isDeactivated) as string)
                : (success(name, isDeactivated) as string)
            }
            variant="success"
          />
        ),
        {
          duration: TOAST_DURATION,
        }
      );
    },
    [addToast, name, success, successUndo, undo]
  );

  const addFailureToast = useCallback(
    (retryAction?: () => void) => {
      addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            adornmentAfter={
              retryAction ? (
                <ToastButton
                  onPress={() => {
                    retryAction();
                    closeToast();
                  }}
                >
                  {retry}
                </ToastButton>
              ) : null
            }
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={failure}
            variant="error"
          />
        ),
        {
          duration: TOAST_DURATION,
        }
      );
    },
    [addToast, failure, retry]
  );

  const getUndoAction = useCallback(
    ({ isDeactivated }: { isDeactivated: boolean }) => {
      const undoAction = isDeactivated ? activateProject : deactivateProject;
      return () => {
        undoAction({
          onCompleted: () => {
            addSuccessToast({ isDeactivated, isUndo: true });
          },
          onError: () => {
            addFailureToast();
          },
        });
      };
    },
    [activateProject, deactivateProject, addSuccessToast, addFailureToast]
  );

  const handleStatusToggleClick = useCallback(() => {
    const isDeactivated = !archivedAt;
    const undoAction = getUndoAction({ isDeactivated });
    const toggleAction = isDeactivated ? deactivateProject : activateProject;
    toggleAction({
      onCompleted: () => {
        track(
          'project-active-status-updated',
          {
            project_id: id,
            is_active: !isDeactivated,
            source: trackingSource,
          },
          { logToDatadog: true }
        );
        addSuccessToast({
          undoAction,
          isDeactivated,
        });
      },
      onError: () => {
        addFailureToast(() =>
          toggleAction({
            onCompleted: () => {
              addSuccessToast({
                undoAction,
                isDeactivated,
              });
            },
            onError: () => {
              addFailureToast();
            },
          })
        );
      },
    });
  }, [
    activateProject,
    addFailureToast,
    addSuccessToast,
    archivedAt,
    deactivateProject,
    getUndoAction,
    id,
    track,
    trackingSource,
  ]);
  return {
    handleStatusToggleClick,
  };
};
